import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserHistory } from 'history';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ThemeProvider } from 'styled-components';
import { Router } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import * as Sentry from '@sentry/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import interceptorCreator from '@ubeya/shared/utils/refreshTokenInterceptor';
import GlobalStyle from '@ubeya/shared-web/style/global';
import themeMaker from '@ubeya/shared-web/style/themeMaker';
import App from './app';
import initNetworkErrorReporter from './shared/utils/networkErrorReporter';

const history = createBrowserHistory();
interceptorCreator('admin');

const REACT_QUERY_CONFIG = {
  defaultOptions: { queries: { staleTime: Infinity, refetchOnWindowFocus: false, cacheTime: 0 } }
};

const theme = themeMaker();

const queryClient = new QueryClient(REACT_QUERY_CONFIG);

if (process.env.ENV === 'PROD' && !process.env.WEB_URL?.includes('reactottavio')) {
  Sentry.init({
    environment: process.env.ENV,
    dsn: 'https://f0bb82396497207fcee98ad62f71a36d@o4508040009220096.ingest.de.sentry.io/4508436961362000',
    integrations: [
      // Sentry.browserTracingIntegration()
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 0.1, //  Capture 10% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0, // Disable replays for non-error page loads
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event, hint) {
      // Filter out AbortError
      const error = hint.originalException;
      if (error && (error.name === 'AbortError' || error.message?.includes('AbortError'))) {
        return null; // Don't send to Sentry
      }
      return event;
    }
  });

  initNetworkErrorReporter();
}

// norma env-preview element inspector — DEV builds only. `process.env.ENV` is inlined by webpack's
// DefinePlugin, so STAGING/PROD/LOCAL fold this to `if (false)` and dead-code-eliminate the module;
// only the DEV build (ottavio ephemeral envs, which norma previews) ships any inspector code. The
// module itself no-ops unless the page is opened with `?norma-inspect=1` inside an iframe.
if (process.env.ENV === 'DEV' || process.env.ENV === 'LOCAL') {
  require('./shared/utils/normaInspector').initNormaInspector();
  require('./shared/utils/normaLocationBridge').initNormaLocationBridge();
}

const container = document.getElementById('root');

const root = createRoot(container);

root.render(
  <ThemeProvider theme={theme}>
    <Router history={history}>
      <GlobalStyle />
      <RecoilRoot>
        <QueryClientProvider client={queryClient}>
          {/* <ReactQueryConfigProvider config={REACT_QUERY_CONFIG}> */}
          <DndProvider backend={HTML5Backend}>
            <App />
          </DndProvider>
        </QueryClientProvider>
      </RecoilRoot>
    </Router>
  </ThemeProvider>
);
